import type { ExitPolicy } from './ExitPolicy'
import type { DrawContextLike } from './exitTypes'
import type {
  EventHandler,
  LotteryEvent,
  LotteryMachineConfig,
  MachineId,
  MachineRuntime,
  MachineState,
  DrawSession,
} from './types'
import type { MachineWorld } from '../physics/MachineWorld'

export interface StateMachineDeps {
  config: LotteryMachineConfig
  session: DrawSession
  machineId: MachineId
  world: MachineWorld
  policy: ExitPolicy
  emit: EventHandler
  getTargetNumber: () => number | undefined
  onMachineComplete: (numbers: number[]) => void
}

/**
 * Idle → LoadBalls → DropIn → MixWarmup → Mixing → ReadyToDraw
 *   ↔ DrawOne → BallCaptured → … → ShowResult → WindDown → Finished
 */
export class DrawStateMachine {
  runtime: MachineRuntime
  private capturingBallId: string | null = null
  private captureElapsed = 0
  private drawnNumbers: number[] = []
  private finished = false

  constructor(private readonly deps: StateMachineDeps) {
    this.runtime = {
      machineId: deps.machineId,
      state: 'Idle',
      mixElapsed: 0,
      drawIndex: 0,
      diskOmega: 0,
      gateExitOpen: false,
      retryCount: 0,
      stateElapsed: 0,
    }
  }

  getDrawn(): number[] {
    return [...this.drawnNumbers]
  }

  isFinished(): boolean {
    return this.finished
  }

  start(): void {
    this.setState('LoadBalls')
  }

  update(dt: number): void {
    if (this.finished || this.runtime.state === 'Error') return

    this.runtime.stateElapsed += dt
    this.runtime.diskOmega = this.deps.world.mixer.getOmega()
    this.runtime.gateExitOpen = this.deps.world.gates.isExitOpen()

    const { timing } = this.deps.config
    const ctx = this.makeCtx(dt)

    switch (this.runtime.state) {
      case 'LoadBalls':
        this.deps.world.gates.setPreviewOpen(false)
        this.deps.world.gates.setExitOpen(false)
        // 落球前先转盘加速
        this.deps.world.mixer.setTargetOmega(this.deps.config.diskOmegaTarget)
        this.setState('DropIn')
        break

      case 'DropIn': {
        // 转盘先转起来，稍后再开闸落球
        const spinBeforeDrop = Math.min(timing.diskSpinup, 0.85)
        if (this.runtime.stateElapsed >= spinBeforeDrop) {
          this.deps.world.gates.setPreviewOpen(true)
        }
        if (this.runtime.stateElapsed >= timing.dropIn) {
          this.deps.world.markInChamber()
          this.setState('MixWarmup')
        }
        break
      }

      case 'MixWarmup':
        this.deps.world.mixer.setTargetOmega(this.deps.config.diskOmegaTarget)
        if (this.runtime.stateElapsed >= Math.max(0.15, timing.diskSpinup * 0.25)) {
          this.setState('Mixing')
        }
        break

      case 'Mixing':
        this.runtime.mixElapsed += dt
        this.deps.emit({
          type: 'mix_progress',
          machineId: this.deps.machineId,
          ratio: Math.min(1, this.runtime.mixElapsed / timing.mix),
        })
        if (this.runtime.mixElapsed >= timing.mix) {
          this.setState('ReadyToDraw')
        }
        break

      case 'ReadyToDraw':
        this.deps.world.mixer.setTargetOmega(this.deps.config.diskOmegaTarget)
        if (this.runtime.stateElapsed >= timing.betweenDraws) {
          this.setState('DrawOne')
        }
        break

      case 'DrawOne': {
        if (this.runtime.stateElapsed < timing.gateOpenAnim) {
          this.deps.world.gates.setExitOpen(true)
          this.deps.policy.onGateOpened(ctx)
        } else {
          this.deps.world.gates.setExitOpen(true)
        }

        const target = this.deps.getTargetNumber()
        if (target != null) {
          const ball = this.deps.world.getEntities().find((e) => e.number === target)
          if (ball) this.deps.policy.assistTowardExit?.(ball, ctx)
        }

        const hit = this.deps.world.pollExitCandidates((b) =>
          this.deps.policy.allowExit(b, this.makeCtx(dt)),
        )

        if (hit) {
          try {
            const num = this.deps.policy.resolveNumber(hit, this.makeCtx(dt))
            this.deps.world.gates.setExitOpen(false)
            this.deps.world.captureBall(hit.id)
            this.capturingBallId = hit.id
            this.captureElapsed = 0
            this.runtime.lastExitedBallId = hit.id
            this.runtime.lastExitedNumber = num
            this.runtime.retryCount = 0
            this.drawnNumbers.push(num)
            this.deps.emit({
              type: 'ball_exit',
              machineId: this.deps.machineId,
              number: num,
              index: this.runtime.drawIndex,
            })
            this.setState('BallCaptured')
          } catch (e) {
            this.fail('RESOLVE_FAIL', e instanceof Error ? e.message : String(e))
          }
        } else if (this.runtime.stateElapsed >= timing.drawTimeout) {
          this.runtime.retryCount += 1
          this.deps.emit({
            type: 'draw_timeout',
            machineId: this.deps.machineId,
            retry: this.runtime.retryCount,
          })
          if (this.runtime.retryCount > this.deps.config.maxDrawRetry) {
            this.fail('DRAW_TIMEOUT', '出球超时，请重置重试')
          } else {
            this.deps.world.mixer.boost(
              1.18,
              timing.retryBoost,
              this.deps.world.getSimTime(),
            )
            this.runtime.stateElapsed = 0
          }
        }
        break
      }

      case 'BallCaptured': {
        this.captureElapsed += dt
        const travel = timing.capture + timing.railTravel
        const t = Math.min(1, this.captureElapsed / travel)
        if (this.capturingBallId) {
          this.deps.world.animateCapturedToDisplay(this.capturingBallId, t)
        }
        if (this.captureElapsed >= travel + timing.showHold) {
          if (this.runtime.lastExitedNumber != null) {
            this.deps.emit({
              type: 'ball_displayed',
              machineId: this.deps.machineId,
              number: this.runtime.lastExitedNumber,
              index: this.runtime.drawIndex,
            })
          }
          const need =
            this.deps.machineId === 'red'
              ? this.deps.config.redCount
              : this.deps.config.blueCount
          this.runtime.drawIndex += 1
          if (this.runtime.drawIndex >= need) {
            this.setState('ShowResult')
          } else {
            this.setState('ReadyToDraw')
          }
        }
        break
      }

      case 'ShowResult':
        this.deps.world.gates.setExitOpen(false)
        this.setState('WindDown')
        break

      case 'WindDown':
        this.deps.world.mixer.setTargetOmega(0)
        if (this.runtime.stateElapsed >= timing.diskSpindown) {
          this.setState('Finished')
          this.finished = true
          this.deps.emit({
            type: 'machine_finished',
            machineId: this.deps.machineId,
            numbers: this.getDrawn(),
          })
          this.deps.onMachineComplete(this.getDrawn())
        }
        break

      default:
        break
    }
  }

  private makeCtx(dt: number): DrawContextLike {
    return {
      session: this.deps.session,
      machine: this.runtime,
      config: this.deps.config,
      targetNumber: this.deps.getTargetNumber(),
      balls: this.deps.world.getEntities(),
      dt,
      nudgeBallTowardExit: (id) => this.deps.world.nudgeTowardExit(id),
    }
  }

  private setState(state: MachineState): void {
    this.runtime.state = state
    this.runtime.stateElapsed = 0
    this.deps.emit({
      type: 'machine_state',
      machineId: this.deps.machineId,
      state,
    })
  }

  private fail(code: string, message: string): void {
    this.runtime.state = 'Error'
    this.deps.session.status = 'error'
    this.deps.session.error = { code, message }
    this.deps.emit({ type: 'error', code, message })
  }
}

export type { LotteryEvent }
